import React, { useEffect, useMemo, useState } from "react";
import {
  FaSave,
  FaTimes,
  FaEdit,
  FaLayerGroup,
  FaQuestionCircle,
  FaAlignLeft,
  FaSpinner,
} from "react-icons/fa";
import { getFaqById, updateFaq } from "../../../../services/faqServices";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const initialFormData = {
  categorie: "",
  titre: "",
  contenu: "",
};

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:ring-indigo-900/40";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
];

const EditFaqModal = ({ isOpen, onClose, faqId, refreshList }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    const fetchFaq = async () => {
      if (!isOpen || !faqId) return;

      try {
        setLoading(true);

        const data = await getFaqById(faqId);

        if (data) {
          setFormData({
            categorie: data.categorie || "",
            titre: data.titre || "",
            contenu: data.contenu || "",
          });
        }
      } catch (error) {
        console.error("Erreur lors du chargement de la FAQ :", error);
        toast.error("Erreur lors du chargement de la FAQ");
      } finally {
        setLoading(false);
      }
    };

    fetchFaq();
  }, [isOpen, faqId]);

  const answerPreview = useMemo(() => {
    if (!formData.contenu) return "Aucune réponse";
    return "Réponse renseignée";
  }, [formData.contenu]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleContenuChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      contenu: value,
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    if (!faqId) {
      toast.error("FAQ introuvable.");
      return;
    }

    if (!formData.categorie.trim() || !formData.titre.trim()) {
      toast.error("Veuillez renseigner au moins la catégorie et la question.");
      return;
    }

    try {
      setSaving(true);

      await updateFaq(faqId, formData);

      toast.success("FAQ mise à jour avec succès");
      refreshList?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur update FAQ :", error);
      toast.error("Erreur lors de la mise à jour de la FAQ");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1200]">
      <div
        className="absolute inset-0 bg-slate-950/70 backdrop-blur-[3px]"
        onClick={onClose}
      />

      <div className="absolute inset-0 flex items-center justify-center p-3 sm:p-6">
        <div className="relative flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-[28px] border border-white/10 bg-white shadow-[0_30px_90px_-30px_rgba(0,0,0,0.45)] dark:bg-slate-900">
          <div className="relative overflow-hidden border-b border-slate-200 dark:border-slate-800">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(99,102,241,0.26),transparent_28%),radial-gradient(circle_at_top_right,rgba(236,72,153,0.22),transparent_24%),linear-gradient(135deg,#0f172a_0%,#1e293b_45%,#312e81_100%)]" />

            <div className="relative px-5 py-5 text-white sm:px-6 sm:py-6">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-white/80">
                    <FaEdit />
                    Modification
                  </div>

                  <h3 className="mt-3 text-2xl font-semibold tracking-tight">
                    Modifier la FAQ
                  </h3>

                  <p className="mt-1 max-w-2xl text-sm text-white/75">
                    Mets à jour la catégorie, la question et la réponse de cette
                    FAQ.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/15 bg-white/10 text-white transition hover:bg-white/20"
                  aria-label="Fermer"
                >
                  <FaTimes />
                </button>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto bg-slate-50 dark:bg-slate-950/60">
            {loading ? (
              <div className="flex min-h-[420px] items-center justify-center p-8">
                <div className="inline-flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm font-medium text-slate-600 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
                  <FaSpinner className="animate-spin" />
                  Chargement de la FAQ...
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-4 sm:p-6">
                <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
                  <div className="space-y-6 xl:col-span-8">
                    <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                      <SectionTitle
                        title="Informations principales"
                        subtitle="Catégorie et question affichée dans la FAQ."
                      />

                      <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
                        <Field>
                          <Label required>Catégorie</Label>

                          <div className="relative">
                            <FaLayerGroup className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                              className={`${inputClass} pl-10`}
                              type="text"
                              name="categorie"
                              value={formData.categorie}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </Field>

                        <Field>
                          <Label required>Question</Label>

                          <div className="relative">
                            <FaQuestionCircle className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                              className={`${inputClass} pl-10`}
                              type="text"
                              name="titre"
                              value={formData.titre}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </Field>
                      </div>
                    </section>

                    <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                      <SectionTitle
                        title="Réponse"
                        subtitle="Rédige une réponse claire, courte et utile."
                      />

                      <div className="mt-5">
                        <Label>Contenu de la réponse</Label>

                        <div className="mt-2 overflow-hidden rounded-[20px] border border-slate-200 bg-white shadow-sm dark:border-slate-700">
                          <ReactQuill
                            value={formData.contenu}
                            onChange={handleContenuChange}
                            modules={quillModules}
                            formats={quillFormats}
                            className="faq-quill"
                            theme="snow"
                          />
                        </div>
                      </div>
                    </section>
                  </div>

                  <div className="space-y-6 xl:col-span-4">
                    <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                      <SectionTitle
                        title="Résumé"
                        subtitle="Aperçu rapide avant enregistrement."
                      />

                      <div className="mt-5 space-y-4">
                        <SummaryRow
                          icon={<FaLayerGroup />}
                          label="Catégorie"
                          value={formData.categorie || "Non renseignée"}
                        />
                        <SummaryRow
                          icon={<FaQuestionCircle />}
                          label="Question"
                          value={formData.titre || "Non renseignée"}
                        />
                        <SummaryRow
                          icon={<FaAlignLeft />}
                          label="Réponse"
                          value={answerPreview}
                        />
                      </div>
                    </section>

                    <section className="rounded-[24px] border border-emerald-200 bg-emerald-50 p-5 text-emerald-900 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-200">
                      <div className="text-sm font-semibold">
                        Modification sécurisée
                      </div>

                      <p className="mt-2 text-sm leading-6 opacity-80">
                        Les changements seront visibles après sauvegarde et
                        rafraîchissement de la liste.
                      </p>
                    </section>
                  </div>
                </div>
              </form>
            )}
          </div>

          <div className="border-t border-slate-200 bg-white/95 px-4 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95 sm:px-6">
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving || loading}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-600 via-violet-600 to-pink-600 px-4 py-2.5 text-sm font-medium text-white transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaSave />
                {saving ? "Enregistrement..." : "Sauvegarder"}
              </button>
            </div>
          </div>

          <QuillStyles />
        </div>
      </div>
    </div>
  );
};

function SectionTitle({ title, subtitle }) {
  return (
    <div>
      <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
        {title}
      </h4>

      {subtitle ? (
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {subtitle}
        </p>
      ) : null}
    </div>
  );
}

function Field({ children, className = "" }) {
  return <div className={className}>{children}</div>;
}

function Label({ children, required = false }) {
  return (
    <label className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200">
      {children}
      {required ? <span className="ml-1 text-rose-500">*</span> : null}
    </label>
  );
}

function SummaryRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-800/50">
      <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-slate-700 shadow-sm dark:bg-slate-900 dark:text-slate-200">
        {icon}
      </div>

      <div className="min-w-0">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
          {label}
        </div>

        <div className="mt-1 break-words text-sm font-medium text-slate-900 dark:text-slate-50">
          {value || "—"}
        </div>
      </div>
    </div>
  );
}

function QuillStyles() {
  return (
    <style>{`
      .faq-quill .ql-toolbar.ql-snow {
        border: 0;
        border-bottom: 1px solid rgb(226 232 240);
        padding: 12px;
        background: linear-gradient(to right, rgba(248,250,252,0.9), rgba(241,245,249,0.9));
      }

      .faq-quill .ql-container.ql-snow {
        border: 0;
        min-height: 190px;
        font-size: 0.95rem;
      }

      .faq-quill .ql-editor {
        min-height: 190px;
        color: #0f172a;
      }

      .dark .faq-quill .ql-toolbar.ql-snow {
        background: rgba(15, 23, 42, 0.92);
        border-bottom: 1px solid rgb(51 65 85);
      }

      .dark .faq-quill .ql-container.ql-snow {
        background: rgb(15 23 42);
      }

      .dark .faq-quill .ql-editor {
        color: #e2e8f0;
      }

      .dark .faq-quill .ql-stroke {
        stroke: #cbd5e1;
      }

      .dark .faq-quill .ql-fill {
        fill: #cbd5e1;
      }

      .dark .faq-quill .ql-picker {
        color: #cbd5e1;
      }
    `}</style>
  );
}

export default EditFaqModal;